import React, { useEffect, useMemo } from "react";
import { Col, Row } from "reactstrap";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import { withTranslation } from "react-i18next";

import UtilityAddressView from "Components/Common/ViewApplicationDetailComponents/UtilityAddressView";
import PersonalDetailsView from "Components/Common/ViewApplicationDetailComponents/PersonalDetailsView";
import ConnectionLoadView from "Components/Common/ViewApplicationDetailComponents/ConnectionLoadView";
import DcsDemandView from "Components/Common/ViewApplicationDetailComponents/DcsDemandView";
import OtherApplicationExtraFieldsView from "Components/Common/ViewApplicationDetailComponents/OtherApplicationExtraFieldsView.js";
import MeterRequisitionView from "Components/Common/ViewApplicationDetailComponents/MeterRequisitionView";
import SewageDemandView from "Components/Common/ViewApplicationDetailComponents/SewageDemandView";
import Backdrop from "Components/Common/Backdrop";
import WaterDemandView from "Components/Common/ViewApplicationDetailComponents/WaterDemandView";
import { getOptionLabel, getOptionValue } from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useLazyConsumerByIdQuery,
  useLazyGetOtherConsumerByIdQuery,
} from "apis/User";

const UtilityConsumerViewModal = ({
  show,
  handleClose,
  utilityConsumerId,
  utilityId,
  t,
}) => {
  const [
    getUtilityConsumerDataByConsumerId,
    { data: { data: powerConsumerData } = {}, isLoading },
  ] = useLazyConsumerByIdQuery();

  const [
    getOtherUtilityConsumerDataByConsumerId,
    {
      data: { data: otherConsumerData } = {},
      isLoading: isOtherUtilityConsumerDataLoading,
    },
  ] = useLazyGetOtherConsumerByIdQuery();

  const utilityConsumerData = useMemo(
    () => powerConsumerData ?? otherConsumerData,
    [powerConsumerData, otherConsumerData]
  );

  useEffect(() => {
    (async () => {
      if (utilityId === getOptionValue("utilityOptions", "Power")) {
        await getUtilityConsumerDataByConsumerId(utilityConsumerId);
      } else {
        await getOtherUtilityConsumerDataByConsumerId(utilityConsumerId);
      }
    })();
  }, [utilityConsumerId, utilityId]);

  if (isLoading || isOtherUtilityConsumerDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      <DialogTitle
        id="scroll-dialog-title"
        className="border-bottom mb-3 fw-bold fs-3"
      >
        <div className="d-flex justify-content-between">
          <div>{t("Utility Consumer Details")}</div>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          variant="subtitle2"
          id="scroll-dialog-description"
          component="div"
        >
          <div className="mt-2">
            <Row className=" mb-2">
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Consumer Name")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {utilityConsumerData?.consumerName}
                  </Col>
                </Row>
              </Col>
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Consumer Number")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {utilityConsumerData?.consumerNumber ?? "-"}
                  </Col>
                </Row>
              </Col>

              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Utility")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {getOptionLabel("utilityOptions", utilityId)}
                  </Col>
                </Row>
              </Col>

              {utilityId === getOptionValue("utilityOptions", "Power") && (
                <Col lg={6} className="mb-1">
                  <Row>
                    <Col md={5}>{t("Source Of Power")}:</Col>
                    <Col md={7} className="fw-semibold">
                      {utilityConsumerData?.sourceOfPower?.name ?? "-"}
                    </Col>
                  </Row>
                </Col>
              )}

              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Connection Date")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {utilityConsumerData?.dateOfConnection
                      ? convertUTCDateToLocalDate(
                          utilityConsumerData.dateOfConnection
                        ).format("DD-MM-YYYY")
                      : "-"}
                  </Col>
                </Row>
              </Col>
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Disconnection Date")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {utilityConsumerData?.disconnectionDate
                      ? convertUTCDateToLocalDate(
                          utilityConsumerData.disconnectionDate
                        ).format("DD-MM-YYYY")
                      : "-"}
                  </Col>
                </Row>
              </Col>
              
              <Col lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t("Sales Order")}:</Col>
                  <Col md={7} className="fw-semibold">
                    {utilityConsumerData?.salesOrder
                      ? utilityConsumerData.salesOrder
                      : "-"}
                  </Col>
                </Row>
              </Col>

              {utilityId ===
                getOptionValue("utilityOptions", "Solid Waste") && (
                <Col lg={6} className="mb-1">
                  <Row>
                    <Col md={5}>{t("Serial Number")}:</Col>
                    <Col md={7} className="fw-semibold">
                      {utilityConsumerData?.serialNumber
                        ? utilityConsumerData.serialNumber
                        : "-"}
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
            {!!utilityConsumerData?.applicant && (
              <PersonalDetailsView applicant={utilityConsumerData.applicant} />
            )}

            {!!utilityConsumerData?.connectionLoad && (
              <ConnectionLoadView
                applicationData={{
                  tariff: {
                    name: utilityConsumerData.tariffName,
                  },
                  connectionLoad: utilityConsumerData.connectionLoad,
                }}
              />
            )}

            {(utilityConsumerData?.totalBuildupArea ||
              utilityConsumerData?.totalNumberOfOccupants ||
              utilityConsumerData?.expectedConnectionStartDate ||
              utilityConsumerData?.expectedConnectionEndDate ||
              utilityConsumerData?.tariffName) && (
              <OtherApplicationExtraFieldsView
                extraValues={{
                  tariff: {
                    name: utilityConsumerData.tariffName,
                  },
                  applicationType: { id: utilityConsumerData.applicationType },
                  totalBuildupArea: utilityConsumerData.totalBuildupArea,
                  totalNumberOfOccupants:
                    utilityConsumerData.totalNumberOfOccupants,
                  expectedConnectionStartDate:
                    utilityConsumerData.expectedConnectionStartDate,
                  expectedConnectionEndDate:
                    utilityConsumerData.expectedConnectionEndDate,
                  totalBuildupAreaUnit:
                    utilityConsumerData.totalBuildupAreaUnit,
                  totalNumberOfOccupantsUnit:
                    utilityConsumerData.totalNumberOfOccupantsUnit,
                }}
              />
            )}

            {utilityConsumerData?.dcsDemand && (
              <>
                <DcsDemandView dcsDemand={utilityConsumerData.dcsDemand} />
                <MeterRequisitionView
                  dcsDemand={utilityConsumerData.dcsDemand}
                />
              </>
            )}

            {utilityConsumerData?.sewageDemand && (
              <SewageDemandView
                sewageDemand={utilityConsumerData.sewageDemand}
              />
            )}

            {utilityConsumerData?.waterDemand && (
              <WaterDemandView waterDemand={utilityConsumerData.waterDemand} />
            )}

            {utilityConsumerData?.utilityAddress && (
              <UtilityAddressView
                utilityAddress={utilityConsumerData.utilityAddress}
                utilities={[utilityConsumerData.utility.id]}
              />
            )}
          </div>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

UtilityConsumerViewModal.propTypes = {
  show: PropTypes.bool,
  handleClose: PropTypes.func,
  utilityConsumerId: PropTypes.number,
  utilityId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(UtilityConsumerViewModal);
